import * as $ from 'jquery';
import { inRange, isUndefined } from 'lodash';

import { elementData } from '../util';

// buffer prevents gap at bottom of table
// @see https://app.asana.com/0/0/141100405061100
const ROW_RANGE_BUFFER = 3;

/**
 * Virtual Scroll Plugin
 */
export default class VirtualScroll {
  static NAMESPACE = 'axel-virtual-scroll';
  static ROW_RANGE_BUFFER = ROW_RANGE_BUFFER;

  public opts;
  public label;
  public $;
  private renderer;
  private containerEl: HTMLElement;
  private $tableContentContainer;
  private $table;
  private $tbody;

  constructor(renderer, opts = {}) {
    this.$ = $;
    this.opts = opts;
    this.renderer = renderer;
    this.label = 'virtual-scroll';
  }

  /**
   * [HookMethod] Initialize
   *
   * Overrides table body layout method
   * @param renderer
   * @param containerEl
   * @returns {any}
   */
  public initialize(renderer, containerEl) {
    this.containerEl = containerEl;
    renderer.buildBody(tableBodyLayoutFrame);
    return renderer;
  }

  /**
   * [HookMethod] Build Table
   * @todo destroy this scroll event listener when the table is destroyed
   * @param tableEl
   * @param containerEl
   * @param opts
   * @returns {any}
   */
  public buildTable(tableEl, containerEl, opts: any = {}) {
    const { $tableContentContainer, $tableContainer } = tableBodyContainer(tableEl);
    const wrappedTableEl = $tableContainer.get(0);

    this.$tableContentContainer = $tableContentContainer;
    this.$table = this.$(tableEl);
    this.$tbody = this.$table.find('tbody');
    // add plugin class
    $tableContainer.addClass(VirtualScroll.NAMESPACE);
    return wrappedTableEl;
  }

  /**
   * [HookMethod] Before Render
   *
   * Set table dimensions before render
   * @param tableEl
   * @param containerEl
   * @returns {any}
   */
  public beforeRender(tableEl, containerEl) {
    const tableHeight = this.getBodyHeight(this.dimensionsAtCapacity());

    // adjust container to computed max height
    this.$tableContentContainer.height(tableHeight);
    return tableEl;
  }

  /**
   * After Render
   *
   * Populate table and subscribe scroll handler
   * @param tableEl
   * @param containerEl
   * @returns {any}
   */
  public afterRender(tableEl, containerEl) {
    const scrollEventLabel = this.namespace('scroll');
    const updateTableRows = this.updateRows();

    // render initial rows
    updateTableRows();
    // remove any previously subscribed virtual scroll handler
    this.$(containerEl).off(scrollEventLabel);
    // subscribe render update to scroll event
    this.$(containerEl).on(scrollEventLabel, updateTableRows);

    return tableEl;
  }

  /**
   * Namespace
   * @param evtName
   */
  private namespace(evtName) {
    return evtName + '.' + VirtualScroll.NAMESPACE;
  }

  private getBodyHeight(padded = false) {
    const contentHeight = this.contentHeight();
    const padding = padded ? this.height() : 0;
    const calculatedHeight = contentHeight + padding;

    return calculatedHeight;
  }

  /**
   * Update Rows
   *
   * Insert rows into table element from a given range
   * @todo consider wrapping insertRow in _.delay to prevent large tables from locking js.
   * @param tableEl
   * @returns {function(): undefined}
   */
  private updateRows() {
    return () => {
      const offsetRowRange = this.rowRange(this.rowOffset(), VirtualScroll.ROW_RANGE_BUFFER);

      if (offsetRowRange.length) {
        if (!this.dimensionsAtCapacity()) {
          this.renderVirtualRows(offsetRowRange);
        } else if (!this.$tbody.children().length) {
          this.renderAllRows();
        }
      }
    };
  }

  private renderAllRows() {
    this.renderer.data().rows().reduce(this.insertRow.bind(this), this.$tbody);
  }

  private renderVirtualRows(offsetRowRange: any) {
    const $table = this.$table;
    const $tbody = this.$tbody;

    // position table first to prevent any flicker
    $table.css('top', this.position());
    this.cleanUpRows($tbody, offsetRowRange);
    offsetRowRange.forEach(this.insertOrUpdateRow.bind(this, $tbody));
  }

  private rowOffset() {
    const rowHeight = this.height();
    const scrollTop = this.containerEl.scrollTop;

    return Math.ceil(scrollTop / rowHeight);
  }

  /**
   * Row Range
   *
   * Get range of rows that can fit in the current viewport which
   * is retrieved from {VirtualScroll.limit}
   * @param start
   * @param buffer
   * @returns {any}
   */
  private rowRange(start, buffer = 0) {
    const rows = this.renderer.data().rows();
    const limit = this.limit();
    const offset = (limit) => {
      const rowCount = rows.length;
      // @todo rename, below threshold is opaque
      const belowThreshold = limit <= rowCount - start;
      const standardOffset = belowThreshold ? start + limit : start % limit;

      return !belowThreshold && start === 0 ? rowCount : standardOffset;
    };
    const end = offset(limit) > start ? offset(limit) + buffer : offset(limit);

    return rows.slice(start, end);
  }

  private position() {
    const rowHeight = this.height();
    const scrollTop = this.containerEl.scrollTop;

    return scrollTop - (scrollTop % rowHeight);
  }

  /**
   * Insert Or Update Row
   *
   * Update table body element with new row or row content
   * @param $tbody
   * @param rowKey
   * @param i
   */
  private insertOrUpdateRow($tbody, rowKey, i) {
    const data = this.renderer.data();
    const rowLayout = this.renderer.buildRow();
    const cellLayout = this.renderer.buildCell();
    const $rows = $tbody.children();
    const rowIndex = data.rows().indexOf(rowKey);
    const selectedColIndex = this.selectedColumn();
    const rowEl = rowLayout(data.tree, rowKey, rowIndex, cellLayout, selectedColIndex);
    const oldRowEl = $rows.get(i);

    if (oldRowEl) {
      this.updateRow(oldRowEl, rowEl);
    } else {
      if (!$tbody.find('[data-axel-row=' + (rowIndex + 1) + ']').length) {
        $tbody.append(rowEl);
      }
    }
  }

  /**
   * Dimensions Restricted
   *
   * State that represents if the table has been resized below it's
   * natural width and height
   * @todo rethink the function name
   * @returns {any}
   */
  private dimensionsAtCapacity() {
    if ('function' === typeof this.opts.dimensionsAtCapacity) {
      return this.opts.dimensionsAtCapacity(this.containerEl);
    } else {
      return true;
    }
  }

  /**
   * Selected Column
   *
   * Current selected column index
   * @returns {any}
   */
  private selectedColumn() {
    if ('function' === typeof this.opts.selectedCol) {
      return this.opts.selectedCol();
    } else {
      return null;
    }
  }

  /**
   * Insert Row
   *
   * Create and insert row elements into the table body
   * @param $tbody
   * @param rowKey
   * @returns {any}
   */
  private insertRow($tbody, rowKey) {
    const data = this.renderer.data();
    const rowIndex = data.rows().indexOf(rowKey);
    const rowLayout = this.renderer.buildRow();
    const cellLayout = this.renderer.buildCell();

    $tbody.append(rowLayout(data.tree, rowKey, rowIndex, cellLayout));
    return $tbody;
  }

  private contentHeight() {
    // add one for table header row
    return this.renderer.data().rows().length * this.height();
  }

  /**
   * Height
   *
   * Static table row height
   * @returns {number}
   */
  private height() {
    return this.opts.rowHeight || 30;
  }

  private limit() {
    return Math.floor(this.containerEl.offsetHeight / this.height());
  }

  /**
   * Clean Up Rows
   *
   * Removes row elements that do not intersect with the requested offset row
   * range and the rows available in the DOM-or-{{$tbody}}
   * @todo optimize to represent rowKeyRange with only first and last value
   * @todo factor out "intersection difference search" into seperate method
   * @param $tbody
   * @param rowKeyRange
   */
  private cleanUpRows($tbody, rowKeyRange) {
    let start, end;
    const rows = this.renderer.data().rows();
    const $rows = $tbody.children();
    const sampleCell = (rowEl) => rowEl.querySelector('[data-axel-row]');
    const cellRowIndex = (rowEl) => parseInt(elementData(sampleCell(rowEl), 'row'));
    const rowKeyIndex = (rowKey) => rows.indexOf(rowKey);
    const rowKeyRangeIndexes = rowKeyRange.map(rowKeyIndex);
    const domRowIndexes = $rows.toArray().map(cellRowIndex);

    for (let j = 0; j < domRowIndexes[j]; j++) {
      for (let i = 0; i < rowKeyRangeIndexes.length; i++) {
        if (domRowIndexes[j] === rowKeyRangeIndexes[i]) {
          if (isUndefined(start)) {
            start = j;
          }
          if (!isUndefined(start)) {
            end = j;
          }
          j++;
        }
      }
      if (j === rowKeyRangeIndexes.length || ![start, end].some(isUndefined)) {
        break;
      }
    }
    $rows.filter((i) => !inRange(i, start, end + 1)).remove();
  }

  /**
   *
   * Copy content and attrs from one table row to another
   * @param targetRowEl
   * @param sourceRowEl
   */
  private updateRow(targetRowEl, sourceRowEl) {
    const targetCellEls = this.$(targetRowEl).find('th, td').toArray();
    const sourceCellEls = this.$(sourceRowEl).find('th, td').toArray();

    targetCellEls.forEach((_, j) => {
      // update individual table cells
      this.updateElement(this.$(targetCellEls[j]), this.$(sourceCellEls[j]));
    });
  }

  /**
   *
   * Copy content and attrs from one element to another
   * @param $target
   * @param $source
   */
  private updateElement($target, $source) {
    // update element content
    $target.text($source.text());
    // update element attributes
    for (const attr of $source.prop('attributes')) {
      $target.attr(attr['name'], attr['value']);
    }
  }
}

/**
 * Table Body Layout Frame
 *
 * Creates empty tbody element that virtual scroll
 * will render to
 * @returns {HTMLTableSectionElement|HTMLElement}
 */
function tableBodyLayoutFrame() {
  return document.createElement('tbody');
}

/**
 * Table Body Container
 *
 * Wrap table with container elements
 * @param tableEl {HTMLElement}
 * @returns {{$tableContentContainer: JQuery, $tableContainer: JQuery}}
 */
function tableBodyContainer(tableEl: HTMLElement) {
  const $tableContentContainer = $('<div class="js-table-content-container"/>').append(tableEl);
  const $tableContainer = $('<div class="js-table-container-body"/>').append($tableContentContainer);

  return { $tableContentContainer, $tableContainer };
}
